import React, { useEffect, useState } from "react";
import "./App.css";

import { persistor, queryClient, store } from "./Store/store";
import { Router } from "./Router/Router";
import { ThemeProvider } from "./Components/Kit";
import { PersistGate } from "redux-persist/integration/react";
import { Modals } from "./Components/Modals/Modals";
import { ToastContainer } from "react-toastify";
import { useLanguage } from "./Store/modules/language";
import { getCurrentLanguageFromUrl } from "./Utils/urlLanguageUtils";
import { useAppDispatch } from "./Hooks/useStore";
import { setLanguage, syncWithUrl } from "./Store/modules/language";
import { useCheckLogin } from "./Store/modules/auth/checkLogin";
import { useModal } from "./Store/modules/modal/modal";
import { ModalNames } from "./Constants/modalNames";
import { useToast } from "./Store/modules/toast/toast";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { useAuthGoogleCredential } from "./Store/modules/auth/authGoogleCredential";
import { changeFavicon } from "./Utils/changeHeadData";
import settings from "./settings/settings";
import ltkFavicon from "./favicons/lamtakam-favicon.png";
import {
  createReactQueryLocalStoragePersister,
  getRequestFcmPermissionCookie,
} from "./Utils/cookieUtils";
import { QueryClientProvider } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { useGetAnnouncedNotifications } from "./Store/modules/ai/announcedNotifications/announcedNotifications";

const persister = createReactQueryLocalStoragePersister();

persistQueryClient({
  queryClient,
  persister,
});

function AppShell() {
  const { direction, locale } = useLanguage();
  const dispatch = useAppDispatch();
  const { checkLoginData } = useCheckLogin();
  const { dispatchOpenModal,name } = useModal();
  const { dispatchShowToast, dispatchHideToast } = useToast();
  const { dispatchAuthGoogleCredential } = useAuthGoogleCredential();
  const [showGoogleLogin, setShowGoogleLogin] = useState(false);
  const { data: userData } = checkLoginData || {};
  const { getAnnouncedNotificationsData } = useGetAnnouncedNotifications();
  const { announced_unread_notifications } = getAnnouncedNotificationsData?.data || {};

  const [isAppLoaded, setIsAppLoaded] = useState(false);
  useEffect(() => {
    setIsAppLoaded(true);
    window.document.fonts.ready.then(() => {
      const splash = document.getElementById("splash-screen");
      if (splash && isAppLoaded) {
        splash.classList.add("fade-out");
      }
    });
  }, [isAppLoaded]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const showLogin = params.get("showLogin");
    if (userData?.user?.type) {
      if (showLogin === "true" && userData?.user?.type !== "permanent") {
        dispatchOpenModal({ name: ModalNames.LoginModal });
      }
    }
  }, [userData?.user?.type]);

  useEffect(() => {
    document.documentElement.setAttribute("dir", direction);
    document.documentElement.setAttribute("lang", locale);
  }, [direction, locale]);

  useEffect(() => {
    if (!name){
      if (announced_unread_notifications?.total) {
        setTimeout(() => {
          dispatchOpenModal({
            name: ModalNames.AnnouncedNotification,
            modalProps: { ...announced_unread_notifications?.notification[0] },
          });
        }, 3000);
      }
    }
  }, [announced_unread_notifications?.notification, dispatchOpenModal]);

  useEffect(() => {
    const checkInternetStatus = () => {
      if (!navigator.onLine) {
        dispatchShowToast({
          name: "NoInternet",
          modalProps: undefined,
        });
      } else {
        dispatchHideToast();
      }
    };

    const internetCheckInterval = setInterval(checkInternetStatus, 5000);

    return () => clearInterval(internetCheckInterval);
  }, []);

  useEffect(() => {
    const urlLanguage = getCurrentLanguageFromUrl();
    const currentStoredLanguage = localStorage.getItem("i18nextLng");

    if (urlLanguage && urlLanguage !== currentStoredLanguage) {
      localStorage.setItem("i18nextLng", urlLanguage);
      dispatch(setLanguage(urlLanguage));
    }
  }, [dispatch]);

  useEffect(() => {
    const handleUrlChange = () => {
      dispatch(syncWithUrl());
    };

    window.addEventListener("popstate", handleUrlChange);
    return () => window.removeEventListener("popstate", handleUrlChange);
  }, [dispatch]);

  useEffect(() => {
    dispatch(syncWithUrl());
    // changeFavicon(settings.favicon ?? ltkFavicon);
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (announced_unread_notifications?.notification?.length === 0) {
        setShowGoogleLogin(true);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [announced_unread_notifications?.notification]);

  return (
    <ThemeProvider>
      {userData?.user?.type !== "permanent" && showGoogleLogin && (
        <div className="hidden">
          <GoogleLogin
            onSuccess={(credentialResponse) => {
              if (credentialResponse.credential) {
                dispatchAuthGoogleCredential({
                  credential: credentialResponse.credential,
                });
              }
            }}
            useOneTap
          />
        </div>
      )}
      <Router />
      <Modals />
      <ToastContainer rtl={direction === "rtl"} toastClassName="estedad-font" style={{ top: 32 }} />
    </ThemeProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId="712012981766-m97bf6olsh22eb97aj3gpthg9kmufacg.apps.googleusercontent.com">
        <PersistGate persistor={persistor}>
          <AppShell />
        </PersistGate>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  );
}

export default App;
